// src/components/Lms/Pages/CoursePlayerPreview.jsx
// Copia de CoursePlayerStudent para vista previa de instructor
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import {
    FiX,
    FiChevronLeft,
    FiChevronRight,
    FiCheckCircle,
    FiPlayCircle,
    FiHelpCircle,
    FiClipboard,
} from "react-icons/fi";
import { API_BASE } from "../../../api";
import { ENDPOINTS as LMS_ENDPOINTS } from "../Utils/Endpoints";
import { Models } from "../Utils/ModelsLMS";
import "../Styles/CoursePlayer.css";

const formatUrl = (url) => {
    if (!url) return '';

    // YouTube
    const youtubeRegExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const ytMatch = url.match(youtubeRegExp);
    if (ytMatch && ytMatch[2].length === 11) {
        return `https://www.youtube.com/embed/${ytMatch[2]}`;
    }

    // Vimeo
    const vimeoRegExp = /vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/(?:[^\/]*)\/videos\/|album\/(?:\d+)\/video\/|video\/|)(\d+)(?:$|\/|\?)/;
    const vimeoMatch = url.match(vimeoRegExp);
    if (vimeoMatch) {
        return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    }

    return url;
};

const CoursePlayerReviewView = () => {
    const { idCurso, moduleId, contentId, contentType } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const fromCarreraId = useMemo(() => {
        const stateVal = location.state?.fromCarreraId;
        if (stateVal) {
            sessionStorage.setItem(`lms_last_carrera_id_${idCurso}`, stateVal);
            return Number(stateVal);
        }
        const sessionVal = sessionStorage.getItem(`lms_last_carrera_id_${idCurso}`);
        return sessionVal ? Number(sessionVal) : null;
    }, [location.state, idCurso]);

    const SERVER_URL = API_BASE.replace(/\/api\/?$/, "");

    const [course, setCourse] = useState(null);
    const [currentLessonId, setCurrentLessonId] = useState(null);
    const [justCompletedTitle, setJustCompletedTitle] = useState("");
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [lessonSections, setLessonSections] = useState([]);
    const [loadingLessonContent, setLoadingLessonContent] = useState(false);
    const [sidebarModules, setSidebarModules] = useState([]);

    // Estados para contenido actual (lección, cuestionario o examen)
    const [currentContentType, setCurrentContentType] = useState(null); // "LECCION", "CUESTIONARIO", "EXAMEN"
    const [currentContentId, setCurrentContentId] = useState(null);
    const [currentContentTitle, setCurrentContentTitle] = useState("");

    // Estados para cuestionarios y exámenes
    const [quizPreguntas, setQuizPreguntas] = useState([]);
    const [examPreguntas, setExamPreguntas] = useState([]);
    const [respuestasUsuario, setRespuestasUsuario] = useState({}); // { idPregunta: idOpcionSeleccionada }
    const [loadingQuestions, setLoadingQuestions] = useState(false);
    const [quizSubmitted, setQuizSubmitted] = useState(false);
    const [examSubmitted, setExamSubmitted] = useState(false);
    const [examResult, setExamResult] = useState(null);

    // Modal de resultados
    const [showResultsModal, setShowResultsModal] = useState(false);
    const [resultsData, setResultsData] = useState({
        correctas: 0,
        incorrectas: 0,
        calificacion: 0,
        aprobado: false
    });

    // Cargar estructura del curso (módulos + todo el contenido) desde BD
    useEffect(() => {
        const loadCourseStructure = async () => {
            const courseId = Number(idCurso);
            if (!courseId) {
                setCourse(null);
                setSidebarModules([]);
                setCurrentLessonId(null);
                return;
            }

            try {
                const [cursoRes, modulosRes, leccionesRes, cuestionariosRes, examenesRes, moduloContenidoRes] = await Promise.all([
                    axios.get(LMS_ENDPOINTS.cursosById(courseId)),
                    axios.get(LMS_ENDPOINTS.moduloByCurso(courseId)),
                    axios.get(LMS_ENDPOINTS.leccionesByCurso(courseId)),
                    axios.get(LMS_ENDPOINTS.cuestionarioByCurso(courseId)),
                    axios.get(LMS_ENDPOINTS.examenByCurso(courseId)),
                    axios.get(LMS_ENDPOINTS.moduloContenidoByCurso(courseId)),
                ]);

                // Curso
                const cursoDataRaw = cursoRes.data;
                const cursoEncontrado = Array.isArray(cursoDataRaw) ? cursoDataRaw[0] : cursoDataRaw;
                setCourse(cursoEncontrado || null);

                // Listas ya filtradas por el backend
                const modulosData = modulosRes.data || [];
                const leccionesData = leccionesRes.data || [];
                const cuestionariosData = cuestionariosRes.data || [];
                const examenesData = examenesRes.data || [];
                const moduloContenidoData = (moduloContenidoRes.data || []).map(Models.moduloContenido);

                const modulosDelCurso = modulosData.sort((a, b) => (a.orden || a.Orden || 0) - (b.orden || b.Orden || 0));

                const builtModules = modulosDelCurso.map((m) => {
                    const idModulo = m.idModulo || m.IdModulo;
                    const tituloModulo = m.nombre || m.Nombre || "Módulo";

                    const contenidosDelModulo = moduloContenidoData
                        .filter((mc) => mc.idModulo === idModulo && mc.activo)
                        .sort((a, b) => (a.orden || 0) - (b.orden || 0));

                    const contenidoMapeado = contenidosDelModulo
                        .map((mc) => {
                            const tipo = mc.tablaContenido;

                            if (tipo === "Lecciones") {
                                const leccion = leccionesData.find(
                                    (l) => (l.idLeccion || l.IdLeccion) === mc.idAsociado
                                );
                                if (!leccion) return null;

                                return {
                                    tipo: "LECCION",
                                    id: leccion.idLeccion || leccion.IdLeccion,
                                    lessonId: leccion.idLeccion || leccion.IdLeccion,
                                    lessonTitle: leccion.titulo || leccion.Titulo || "Lección",
                                    moduleId: idModulo,
                                    completado: false,
                                };
                            }

                            if (tipo === "Cuestionario") {
                                const cuestionario = cuestionariosData.find(
                                    (q) => (q.idCuestionario || q.IdCuestionario) === mc.idAsociado
                                );
                                if (!cuestionario) return null;

                                return {
                                    tipo: "CUESTIONARIO",
                                    id: cuestionario.idCuestionario || cuestionario.IdCuestionario,
                                    quizId: cuestionario.idCuestionario || cuestionario.IdCuestionario,
                                    lessonTitle: cuestionario.nombre || cuestionario.Nombre || "Cuestionario",
                                    moduleId: idModulo,
                                };
                            }

                            if (tipo === "Examen") {
                                const examen = examenesData.find(
                                    (e) => (e.idExamen || e.IdExamen) === mc.idAsociado
                                );
                                if (!examen) return null;

                                return {
                                    tipo: "EXAMEN",
                                    id: examen.idExamen || examen.IdExamen,
                                    examId: examen.idExamen || examen.IdExamen,
                                    lessonTitle: examen.nombre || examen.Nombre || "Examen",
                                    moduleId: idModulo,
                                };
                            }

                            return null;
                        })
                        .filter(Boolean);

                    return {
                        moduleId: idModulo,
                        moduleTitle: tituloModulo,
                        items: contenidoMapeado,
                    };
                });

                setSidebarModules(builtModules);

                let initialLessonId = contentId || null;
                if (!initialLessonId) {
                    const firstModule = builtModules.find(
                        (m) => (m.items || []).some((item) => item.tipo === "LECCION")
                    );
                    if (firstModule) {
                        const firstLesson = firstModule.items.find((item) => item.tipo === "LECCION");
                        if (firstLesson) {
                            initialLessonId = firstLesson.lessonId;
                        }
                    }
                }
                setCurrentLessonId(contentType === "LECCION" ? initialLessonId : null);
            } catch (error) {
                console.error("[CoursePlayerPreview] Error al cargar estructura de curso:", error);
                setCourse(null);
                setSidebarModules([]);
                setCurrentLessonId(null);
            }
        };

        loadCourseStructure();
    }, [idCurso, contentId, contentType]);

    useEffect(() => {
        console.log('[CoursePlayerReview] useEffect ejecutado - contentId:', contentId, 'contentType:', contentType);

        if (!contentId || !contentType) return;

        if (contentType === 'CUESTIONARIO') {
            console.log('[CoursePlayerReview] Cargando cuestionario con ID:', contentId);
            setCurrentContentType('CUESTIONARIO');
            setCurrentContentId(contentId);
            setCurrentContentTitle('');
            setCurrentLessonId(null);
            setLessonSections([]);
            setRespuestasUsuario({});
            setQuizSubmitted(false); // Modo revisión interactivo
            loadQuizQuestions(contentId);

        } else if (contentType === 'EXAMEN') {
            console.log('[CoursePlayerReview] Cargando examen con ID:', contentId);
            setCurrentContentType('EXAMEN');
            setCurrentContentId(contentId);
            setCurrentContentTitle('');
            setCurrentLessonId(null);
            setLessonSections([]);
            setRespuestasUsuario({});
            setExamSubmitted(false); // Modo revisión interactivo
            loadExamQuestions(contentId);

        } else if (contentType === 'LECCION') {
            console.log('[CoursePlayerReview] Cargando lección con ID:', contentId);
            setCurrentContentType('LECCION');
            setCurrentContentId(contentId);
            setCurrentLessonId(contentId);
        }
    }, [contentId, contentType]);

    // Función para cargar preguntas de un cuestionario
    const loadQuizQuestions = async (cuestionarioId) => {
        console.log('[loadQuizQuestions] Iniciando carga para cuestionario:', cuestionarioId);
        setLoadingQuestions(true);

        try {
            // 1. Obtener todas las preguntas del cuestionario
            const resPreguntas = await axios.get(LMS_ENDPOINTS.cuestionariosPreguntas);
            const todasPreguntas = resPreguntas.data || [];

            const preguntasDelCuestionario = todasPreguntas.filter(
                (p) => (p.idCuestionario || p.IdCuestionario) === parseInt(cuestionarioId, 10)
            );

            // Obtener datos del cuestionario para verificar aleatoriedad
            const resCuestionarios = await axios.get(LMS_ENDPOINTS.cuestionario);
            const cuestionarios = resCuestionarios.data || [];
            const cuestionario = cuestionarios.find(
                q => (q.idCuestionario || q.IdCuestionario) === parseInt(cuestionarioId, 10)
            );

            let preguntasFinales = [...preguntasDelCuestionario];

            if (cuestionario) {
                const cantidadAleatoria = cuestionario.preguntasAleatorias || cuestionario.PreguntasAleatorias || 0;
                if (cantidadAleatoria > 0) {
                    for (let i = preguntasFinales.length - 1; i > 0; i--) {
                        const j = Math.floor(Math.random() * (i + 1));
                        [preguntasFinales[i], preguntasFinales[j]] = [preguntasFinales[j], preguntasFinales[i]];
                    }
                    const cantidadATomar = Math.min(cantidadAleatoria, preguntasFinales.length);
                    preguntasFinales = preguntasFinales.slice(0, cantidadATomar);
                }
            }
            console.log('[loadQuizQuestions] Preguntas seleccionadas:', preguntasFinales.length);

            console.log('[loadQuizQuestions] Preguntas seleccionadas:', preguntasFinales.length);

            // 2. Cargar respuestas de texto libre y verdadero/falso
            let todasRespuestasTextoLibre = [];
            let todasRespuestasVF = [];
            try {
                const [resTextoLibre, resVF] = await Promise.all([
                    axios.get(LMS_ENDPOINTS.cuestionarioRespuestaTextoLibre),
                    axios.get(LMS_ENDPOINTS.cuestionarioRespuestaVerdaderoFalso)
                ]);
                todasRespuestasTextoLibre = (resTextoLibre.data || []).map(Models.cuestionarioRespuestaTextoLibre);
                todasRespuestasVF = (resVF.data || []).map(Models.cuestionarioRespuestaVerdaderoFalso);
                console.log('[loadQuizQuestions] Respuestas texto libre:', todasRespuestasTextoLibre.length, 'V/F:', todasRespuestasVF.length);
            } catch (err) {
                console.error('[loadQuizQuestions] Error cargando respuestas:', err);
            }

            // 3. Por cada pregunta, cargar sus respuestas/opciones
            const preguntasConOpciones = await Promise.all(
                preguntasFinales.map(async (pregunta) => {
                    const idPregunta = pregunta.idCuestionarioPreguntas || pregunta.IdCuestionarioPreguntas || pregunta.idPregunta || pregunta.IdPregunta;
                    const idTipoPreguntaRaw = pregunta.idTipoPregunta || pregunta.IdTipoPregunta || pregunta.idCatTipoPregunta || pregunta.IdCatTipoPregunta || 0;
                    const idTipoPregunta = parseInt(idTipoPreguntaRaw, 10);

                    console.log(`[loadQuizQuestions] Procesando pregunta ${idPregunta}, tipo: ${idTipoPregunta}`);

                    try {
                        // Si es texto libre (tipo 1)
                        if (idTipoPregunta === 1) {
                            const respTextoLibre = todasRespuestasTextoLibre.find(
                                (r) => r.idCuestionarioPreguntas === idPregunta
                            ) || null;

                            return {
                                ...pregunta,
                                idPregunta: idPregunta,
                                tipo: "textoLibre",
                                respuestaTextoLibre: respTextoLibre,
                                opciones: [],
                            };
                        }

                        // Si es Verdadero/Falso (tipo 3)
                        if (idTipoPregunta === 3) {
                            const respVF = todasRespuestasVF.find(
                                (r) => (r.idCuestionarioPreguntas || r.IdCuestionarioPreguntas) === idPregunta
                            ) || null;

                            return {
                                ...pregunta,
                                idPregunta: idPregunta,
                                tipo: "verdaderoFalso",
                                respuestaVerdaderoFalso: respVF,
                                opciones: [],
                            };
                        }

                        // Si es opción múltiple (tipo 2)
                        const resRespuestas = await axios.get(LMS_ENDPOINTS.cuestionarioRespuestaOpcionMultiple);
                        const todasRespuestas = resRespuestas.data || [];

                        const respuesta = todasRespuestas.find(
                            (r) => (r.idCuestionarioPreguntas || r.IdCuestionarioPreguntas) === idPregunta
                        );

                        if (respuesta) {
                            const idRespuesta = respuesta.idRespuestaOpcionMultiple || respuesta.IdRespuestaOpcionMultiple;

                            // Cargar opciones
                            const resOpciones = await axios.get(LMS_ENDPOINTS.cuestionarioRespuestaOpcionMultipleOpciones);
                            const todasOpciones = resOpciones.data || [];

                            const opcionesOrdenadas = todasOpciones
                                .filter((o) => (o.idRespuestaOpcionMultiple || o.IdRespuestaOpcionMultiple) === idRespuesta)
                                .sort((a, b) => (a.orden || a.Orden || 0) - (b.orden || b.Orden || 0));

                            const opciones = opcionesOrdenadas.map((o, idx) => ({
                                ...o,
                                idOpcion: o.idOpcion || o.IdOpcion,
                                llave: String.fromCharCode(65 + idx),
                                descripcion: o.texto || o.Texto || "",
                                texto: o.texto || o.Texto || "",
                                esCorrecta: o.esCorrecta !== undefined ? o.esCorrecta : (o.EsCorrecta || false),
                                orden: o.orden || o.Orden || idx + 1,
                            }));

                            return {
                                ...pregunta,
                                idPregunta: idPregunta,
                                tipo: "opcionMultiple",
                                opciones,
                                idRespuesta,
                                permiteMultiple: respuesta.permiteMultiple || respuesta.PermiteMultiple || false,
                            };
                        }

                        return { ...pregunta, idPregunta: idPregunta, tipo: "opcionMultiple", opciones: [] };
                    } catch (error) {
                        console.error(`[loadQuizQuestions] Error cargando opciones para pregunta ${idPregunta}:`, error);
                        return { ...pregunta, idPregunta: idPregunta, opciones: [] };
                    }
                })
            );

            console.log('[loadQuizQuestions] Preguntas procesadas:', preguntasConOpciones);
            setQuizPreguntas(preguntasConOpciones);
        } catch (error) {
            console.error('[loadQuizQuestions] Error al cargar preguntas del cuestionario:', error);
            setQuizPreguntas([]);
        } finally {
            setLoadingQuestions(false);
        }
    };

    // Función para cargar preguntas de un examen
    const loadExamQuestions = async (examenId) => {
        console.log('[loadExamQuestions] Iniciando carga para examen:', examenId);
        setLoadingQuestions(true);

        try {
            // 1. Obtener todas las preguntas del examen
            const resPreguntas = await axios.get(LMS_ENDPOINTS.examenPreguntas);
            const todasPreguntas = resPreguntas.data || [];

            const preguntasDelExamen = todasPreguntas.filter(
                (p) => (p.idExamen || p.IdExamen) === parseInt(examenId, 10)
            );

            // Obtener datos del examen para verificar aleatoriedad
            const resExamenes = await axios.get(LMS_ENDPOINTS.examen);
            const examenes = resExamenes.data || [];
            const examen = examenes.find(
                e => (e.idExamen || e.IdExamen) === parseInt(examenId, 10)
            );

            let preguntasFinales = [...preguntasDelExamen];

            if (examen) {
                const cantidadAleatoria = examen.preguntasAleatorias || examen.PreguntasAleatorias || 0;
                if (cantidadAleatoria > 0) {
                    for (let i = preguntasFinales.length - 1; i > 0; i--) {
                        const j = Math.floor(Math.random() * (i + 1));
                        [preguntasFinales[i], preguntasFinales[j]] = [preguntasFinales[j], preguntasFinales[i]];
                    }
                    const cantidadATomar = Math.min(cantidadAleatoria, preguntasFinales.length);
                    preguntasFinales = preguntasFinales.slice(0, cantidadATomar);
                }
            }
            console.log('[loadExamQuestions] Preguntas seleccionadas:', preguntasFinales.length);

            console.log('[loadExamQuestions] Preguntas seleccionadas:', preguntasFinales.length);

            // 2. Cargar respuestas de texto libre y verdadero/falso (de las preguntas)
            let todasRespuestasTextoLibre = [];
            let todasRespuestasVF = [];
            try {
                const [resTextoLibre, resVF] = await Promise.all([
                    axios.get(LMS_ENDPOINTS.examenPreguntaTextoLibre),
                    axios.get(LMS_ENDPOINTS.examenPreguntaVerdaderoFalso)
                ]);
                todasRespuestasTextoLibre = (resTextoLibre.data || []).map(Models.examenPreguntaTextoLibre);
                todasRespuestasVF = (resVF.data || []).map(Models.examenPreguntaVerdaderoFalso);
                console.log('[loadExamQuestions] Respuestas texto libre:', todasRespuestasTextoLibre.length, 'V/F:', todasRespuestasVF.length);
            } catch (err) {
                console.error('[loadExamQuestions] Error cargando respuestas:', err);
            }

            // 3. Por cada pregunta, cargar sus respuestas/opciones
            const preguntasConOpciones = await Promise.all(
                preguntasFinales.map(async (pregunta) => {
                    const idPregunta = pregunta.idExamenPregunta || pregunta.IdExamenPregunta || pregunta.idExamenPreguntas || pregunta.IdExamenPreguntas || pregunta.idPregunta || pregunta.IdPregunta;
                    const idTipoPreguntaRaw = pregunta.idTipoPregunta || pregunta.IdTipoPregunta || pregunta.idCatTipoPregunta || pregunta.IdCatTipoPregunta || 0;
                    const idTipoPregunta = parseInt(idTipoPreguntaRaw, 10);

                    console.log(`[loadExamQuestions] Procesando pregunta ${idPregunta}, tipo: ${idTipoPregunta}`);

                    try {
                        // Si es texto libre (tipo 1)
                        if (idTipoPregunta === 1) {
                            const respTextoLibre = todasRespuestasTextoLibre.find(
                                (r) => r.idExamenPregunta === idPregunta
                            ) || null;

                            console.log(`[loadExamQuestions] Pregunta ${idPregunta} - Respuesta texto libre:`, respTextoLibre);

                            return {
                                ...pregunta,
                                idPregunta: idPregunta,
                                tipo: "textoLibre",
                                respuestaTextoLibre: respTextoLibre,
                                opciones: [],
                            };
                        }

                        // Si es Verdadero/Falso (tipo 3)
                        if (idTipoPregunta === 3) {
                            const respVF = todasRespuestasVF.find(
                                (r) => (r.idExamenPregunta || r.IdExamenPregunta || r.idExamenPreguntas || r.IdExamenPreguntas) === idPregunta
                            ) || null;

                            return {
                                ...pregunta,
                                idPregunta: idPregunta,
                                tipo: "verdaderoFalso",
                                respuestaVerdaderoFalso: respVF,
                                opciones: [],
                            };
                        }

                        // Si es opción múltiple (tipo 2)
                        const resRespuestas = await axios.get(LMS_ENDPOINTS.examenPreguntaOpcionMultiple);
                        const todasRespuestas = resRespuestas.data || [];

                        const respuesta = todasRespuestas.find(
                            (r) => (r.idExamenPregunta || r.IdExamenPregunta) === idPregunta
                        );

                        if (respuesta) {
                            // El ID correcto es idExamenRespuestaOpcionMultiple
                            const idRespuesta = respuesta.idExamenRespuestaOpcionMultiple || respuesta.IdExamenRespuestaOpcionMultiple;

                            console.log(`[loadExamQuestions] Pregunta ${idPregunta} - ID Respuesta OM: ${idRespuesta}`);

                            // Cargar opciones
                            const resOpciones = await axios.get(LMS_ENDPOINTS.examenPreguntaOpcionMultipleOpciones);
                            const todasOpciones = resOpciones.data || [];

                            const opcionesOrdenadas = todasOpciones
                                .filter((o) => (o.idExamenRespuestaOpcionMultiple || o.IdExamenRespuestaOpcionMultiple) === idRespuesta)
                                .sort((a, b) => (a.orden || a.Orden || 0) - (b.orden || b.Orden || 0));

                            const opciones = opcionesOrdenadas.map((o, idx) => ({
                                ...o,
                                idOpcion: o.idExamenOpcion || o.IdExamenOpcion,
                                llave: String.fromCharCode(65 + idx),
                                descripcion: o.texto || o.Texto || "",
                                texto: o.texto || o.Texto || "",
                                esCorrecta: o.esCorrecta !== undefined ? o.esCorrecta : (o.EsCorrecta || false),
                                orden: o.orden || o.Orden || idx + 1,
                            }));

                            console.log(`[loadExamQuestions] Pregunta ${idPregunta} - Opciones cargadas:`, opciones.length);

                            return {
                                ...pregunta,
                                idPregunta: idPregunta,
                                tipo: "opcionMultiple",
                                opciones,
                                idRespuesta,
                                permiteMultiple: respuesta.permiteMultiple || respuesta.PermiteMultiple || false,
                            };
                        }

                        return { ...pregunta, idPregunta: idPregunta, tipo: "opcionMultiple", opciones: [] };
                    } catch (error) {
                        console.error(`[loadExamQuestions] Error cargando opciones para pregunta ${idPregunta}:`, error);
                        return { ...pregunta, idPregunta: idPregunta, opciones: [] };
                    }
                })
            );

            console.log('[loadExamQuestions] Preguntas procesadas:', preguntasConOpciones);
            setExamPreguntas(preguntasConOpciones);
        } catch (error) {
            console.error('[loadExamQuestions] Error al cargar preguntas del examen:', error);
            setExamPreguntas([]);
        } finally {
            setLoadingQuestions(false);
        }
    };


    // Todos los elementos (lecciones, cuestionarios, exámenes) en orden
    const flatAllContent = useMemo(
        () =>
            sidebarModules.flatMap((m) =>
                (m.items || []).map((item) => ({
                    ...item,
                    moduleId: m.moduleId,
                    moduleTitle: m.moduleTitle ||
                        (course ? course.nombre || course.Nombre || course.titulo : ""),
                }))
            ),
        [sidebarModules, course]
    );

    const flatLessons = useMemo(
        () =>
            sidebarModules.flatMap((m) =>
                (m.items || [])
                    .filter((item) => item.tipo === "LECCION")
                    .map((item) => ({
                        ...item,
                        moduleTitle:
                            m.moduleTitle ||
                            (course ? course.nombre || course.Nombre || course.titulo : ""),
                    }))
            ),
        [sidebarModules, course]
    );

    const totalElements = flatLessons.length;

    const completedCount = useMemo(
        () => flatLessons.filter((x) => x.completado).length,
        [flatLessons]
    );

    const topProgress = useMemo(
        () =>
            totalElements === 0
                ? 0
                : Math.round((completedCount / totalElements) * 100),
        [completedCount, totalElements]
    );

    // Índice actual en flatAllContent (incluye todos los tipos)
    const currentIndexInAll = useMemo(() => {
        if (currentContentType === "LECCION" && currentLessonId) {
            return flatAllContent.findIndex(
                (x) => x.tipo === "LECCION" && String(x.lessonId) === String(currentLessonId)
            );
        } else if (currentContentType === "CUESTIONARIO" && currentContentId) {
            return flatAllContent.findIndex(
                (x) => x.tipo === "CUESTIONARIO" && String(x.id) === String(currentContentId)
            );
        } else if (currentContentType === "EXAMEN" && currentContentId) {
            return flatAllContent.findIndex(
                (x) => x.tipo === "EXAMEN" && String(x.id) === String(currentContentId)
            );
        }
        return 0;
    }, [flatAllContent, currentContentType, currentLessonId, currentContentId]);

    const currentIndex = useMemo(() => {
        const idx = flatLessons.findIndex(
            (x) => String(x.lessonId) === String(currentLessonId)
        );
        return idx === -1 ? 0 : idx;
    }, [flatLessons, currentLessonId]);

    const currentItem =
        flatLessons.length > 0 ? flatLessons[currentIndex] || null : null;

    const modulesWithLessons = useMemo(
        () => sidebarModules,
        [sidebarModules]
    );

    const handleClickContent = (item) => {
        const { tipo, id, lessonId: lid, quizId, examId, lessonTitle, moduleId: mId } = item;

        setCurrentContentType(tipo);
        setCurrentContentId(id);
        setCurrentContentTitle(lessonTitle || "");
        setRespuestasUsuario({});

        // Forzar modo revisión según tipo
        if (tipo === "CUESTIONARIO") setQuizSubmitted(true);
        if (tipo === "EXAMEN") setExamSubmitted(true);

        navigate(`/menu/lms/curso-player-revision-view/${idCurso}/${mId}/${id}/${tipo}`);
    };

    const handleClickLesson = (id) => {
        const item = flatLessons.find((l) => l.lessonId === id);
        if (item) {
            handleClickContent(item);
        }
    };

    const handleToggleComplete = () => {
        if (!currentItem) return;

        const targetLessonId = currentItem.lessonId;

        setSidebarModules((prev) => {
            const updated = prev.map((mod) => ({
                ...mod,
                items: (mod.items || []).map((item) =>
                    item.tipo === "LECCION" && String(item.lessonId) === String(targetLessonId)
                        ? { ...item, completado: !item.completado }
                        : item
                ),
            }));

            const flat = updated.flatMap((m) => m.items || []);
            const refreshed = flat.find(
                (x) => x.tipo === "LECCION" && String(x.lessonId) === String(targetLessonId)
            );

            if (refreshed?.lessonTitle) {
                setJustCompletedTitle(
                    refreshed.completado ? refreshed.lessonTitle : ""
                );
                if (refreshed.completado) {
                    setTimeout(() => setJustCompletedTitle(""), 4000);
                }
            }

            return updated;
        });
    };

    const goPrev = () => {
        if (currentIndexInAll <= 0) return;
        const prev = flatAllContent[currentIndexInAll - 1];
        if (prev) handleClickContent(prev);
    };

    const goNext = () => {
        // Usar flatAllContent para navegación correcta
        if (currentIndexInAll >= flatAllContent.length - 1) {
            // Si es la última actividad, redirigir al currículum previo
            navigate(`/menu/lms/curso-curriculo-revision-view/${idCurso}`, { state: { fromCarreraId } });
            return;
        }
        const next = flatAllContent[currentIndexInAll + 1];
        if (next) {
            handleClickContent(next);
        }
    };

    useEffect(() => {
        const targetContentId = contentId || currentLessonId;
        const loadLessonContent = async () => {
            if (!targetContentId || contentType !== "LECCION") {
                setLessonSections([]);
                return;
            }

            setLoadingLessonContent(true);
            try {
                const response = await axios.get(LMS_ENDPOINTS.leccionesContenido);
                const allContent = response.data || [];
                const lessonContent = allContent.filter(
                    (c) =>
                        String(c.idLeccion || c.IdLeccion) === String(targetContentId) &&
                        (c.activo === true ||
                            c.Activo === true ||
                            (c.activo === undefined && c.Activo === undefined))
                );

                const sections = lessonContent
                    .map((item) => {
                        try {
                            const parsed = JSON.parse(item.valor || item.Valor);
                            return {
                                ...parsed,
                                id: item.idContenido || item.IdContenido,
                            };
                        } catch (e) {
                            console.error("[CoursePlayerPreview] Error parsing lesson content valor:", e);
                            return null;
                        }
                    })
                    .filter(Boolean)
                    .sort((a, b) => (a.order || 0) - (b.order || 0));

                setLessonSections(sections);
            } catch (error) {
                console.error("[CoursePlayerPreview] Error loading lesson content:", error);
                setLessonSections([]);
            } finally {
                setLoadingLessonContent(false);
            }
        };

        loadLessonContent();
    }, [contentId, currentLessonId]);

    /* FUNCIÓN VIEJA - REEMPLAZADA POR LA NUEVA QUE CARGA RESPUESTAS DE TEXTO LIBRE
    const loadQuizQuestions = async (quizId) => {
        setLoadingQuestions(true);
        try {
            const resPreguntas = await axios.get(LMS_ENDPOINTS.cuestionariosPreguntas);
            const todasPreguntas = resPreguntas.data || [];

            const preguntasDelCuestionario = todasPreguntas.filter(
                (p) => (p.idCuestionario || p.IdCuestionario) === parseInt(quizId, 10)
            );

            const preguntasConOpciones = await Promise.all(
                preguntasDelCuestionario.map(async (pregunta) => {
                    const idPregunta =
                        pregunta.idCuestionarioPreguntas ||
                        pregunta.IdCuestionarioPreguntas ||
                        pregunta.idCuestionarioPregunta ||
                        pregunta.IdCuestionarioPregunta;

                    try {
                        const resRespuestas = await axios.get(
                            LMS_ENDPOINTS.cuestionarioRespuestaOpcionMultiple
                        );
                        const todasRespuestas = resRespuestas.data || [];

                        const respuesta = todasRespuestas.find(
                            (r) =>
                                (r.idCuestionarioPreguntas ||
                                    r.IdCuestionarioPreguntas ||
                                    r.idCuestionarioPregunta ||
                                    r.IdCuestionarioPregunta) === idPregunta
                        );

                        if (respuesta) {
                            const idRespuesta =
                                respuesta.idRespuestaOpcionMultiple ||
                                respuesta.IdRespuestaOpcionMultiple ||
                                respuesta.idRespuesta ||
                                respuesta.IdRespuesta;

                            const resOpciones = await axios.get(
                                LMS_ENDPOINTS.cuestionarioRespuestaOpcionMultipleOpciones
                            );
                            const todasOpciones = resOpciones.data || [];

                            const opcionesOrdenadas = todasOpciones
                                .filter(
                                    (o) =>
                                        (o.idRespuestaOpcionMultiple ||
                                            o.IdRespuestaOpcionMultiple) === idRespuesta
                                )
                                .sort(
                                    (a, b) => (a.orden || a.Orden || 0) - (b.orden || b.Orden || 0)
                                );

                            const opciones = opcionesOrdenadas.map((o, idx) => ({
                                ...o,
                                idOpcion: o.idOpcion || o.IdOpcion,
                                llave: String.fromCharCode(65 + idx),
                                descripcion: o.texto || o.Texto || "",
                                esCorrecta:
                                    o.esCorrecta !== undefined
                                        ? o.esCorrecta
                                        : o.EsCorrecta !== undefined
                                            ? o.EsCorrecta
                                            : false,
                                orden: o.orden || o.Orden || idx + 1,
                            }));

                            return {
                                ...pregunta,
                                idPregunta,
                                descripcion:
                                    pregunta.descripcionPregunta ||
                                    pregunta.DescripcionPregunta ||
                                    "",
                                opciones,
                                idRespuesta,
                            };
                        }

                        return { ...pregunta, idPregunta, opciones: [] };
                    } catch (error) {
                        console.error(
                            `[CoursePlayerPreview] Error cargando opciones para pregunta ${idPregunta}:`,
                            error
                        );
                        return { ...pregunta, idPregunta, opciones: [] };
                    }
                })
            );

            setQuizPreguntas(preguntasConOpciones);
        } catch (error) {
            console.error("[CoursePlayerPreview] Error al cargar preguntas del cuestionario:", error);
            setQuizPreguntas([]);
        } finally {
            setLoadingQuestions(false);
        }
    };

    const loadExamQuestions = async (examId) => {
        setLoadingQuestions(true);
        try {
            const [resPreguntas, resCabecerasOM, resOpcionesOM] = await Promise.all([
                axios.get(LMS_ENDPOINTS.examenPreguntas),
                axios.get(LMS_ENDPOINTS.examenPreguntaOpcionMultiple),
                axios.get(LMS_ENDPOINTS.examenPreguntaOpcionMultipleOpciones),
            ]);

            const todasPreguntas = (resPreguntas.data || []).map(Models.examenPreguntas);
            const todasCabecerasOM = (resCabecerasOM.data || []).map(
                Models.examenPreguntaOpcionMultiple
            );
            const todasOpcionesOM = (resOpcionesOM.data || []).map(
                Models.examenPreguntaOpcionMultipleOpcion
            );

            const preguntasDelExamen = todasPreguntas.filter(
                (p) => p.idExamen === parseInt(examId, 10)
            );

            const preguntasConOpciones = preguntasDelExamen.map((pregunta) => {
                const idPregunta = pregunta.idExamenPregunta;

                const cabecera = todasCabecerasOM.find(
                    (r) => r.idExamenPregunta === idPregunta
                );

                if (!cabecera) {
                    return { ...pregunta, opciones: [], idRespuesta: null };
                }

                const idRespuestaOM = cabecera.idExamenRespuestaOpcionMultiple;

                const opcionesOrdenadas = todasOpcionesOM
                    .filter((o) => o.idExamenRespuestaOpcionMultiple === idRespuestaOM)
                    .sort((a, b) => (a.orden || 0) - (b.orden || 0));

                const opciones = opcionesOrdenadas.map((o, idx) => ({
                    ...o,
                    idOpcion:
                        o.idOpcion ||
                        o.IdOpcion ||
                        o.idExamenPreguntaOpcionMultiple ||
                        o.IdExamenPreguntaOpcionMultiple ||
                        `${idPregunta}-${idx}`,
                    llave: String.fromCharCode(65 + idx),
                    descripcion: o.texto || "",
                    esCorrecta:
                        o.esCorrecta !== undefined
                            ? o.esCorrecta
                            : o.EsCorrecta !== undefined
                                ? o.EsCorrecta
                                : false,
                    orden: o.orden || idx + 1,
                }));

                return {
                    ...pregunta,
                    idPregunta,
                    descripcion:
                        pregunta.descripcionPregunta || pregunta.DescripcionPregunta || "",
                    opciones,
                    idRespuesta: idRespuestaOM,
                };
            });

            setExamPreguntas(preguntasConOpciones);
        } catch (error) {
            console.error("[CoursePlayerPreview] Error al cargar preguntas del examen:", error);
            setExamPreguntas([]);
        } finally {
            setLoadingQuestions(false);
        }
    };

    */ // FIN DE FUNCIONES VIEJAS - USAR LAS NUEVAS IMPLEMENTADAS ARRIBA
    const renderLessonSections = () => {
        if (loadingLessonContent) {
            return (
                <div style={{ textAlign: "center", padding: "2rem", color: "#64748b" }}>
                    Cargando contenido de la lección...
                </div>
            );
        }

        if (!lessonSections || lessonSections.length === 0) {
            return (
                <div style={{ textAlign: "center", padding: "2rem", color: "#94a3b8" }}>
                    Esta lección aún no tiene contenido configurado.
                </div>
            );
        }

        return lessonSections.map((section, index) => (
            <div
                key={section.id}
                style={{
                    marginBottom: 0,
                    backgroundColor: section.style?.backgroundColor || "#ffffff",
                    padding: section.style?.padding ?? 20,
                    borderRadius:
                        index === 0
                            ? index === lessonSections.length - 1
                                ? "8px"
                                : "8px 8px 0 0"
                            : index === lessonSections.length - 1
                                ? "0 0 8px 8px"
                                : "0",
                }}
            >
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns:
                            section.columns === 2
                                ? `${section.splitRatio || 50}fr ${100 - (section.splitRatio || 50)}fr`
                                : "1fr",
                        gap: section.style?.gap ?? 20,
                    }}
                >
                    {(section.children || []).map((column, colIdx) => (
                        <div key={colIdx} style={{
                            minHeight: 40,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center'
                        }}>
                            {(column.children || []).length === 0 && (
                                <div
                                    style={{
                                        textAlign: "center",
                                        color: "#cbd5f5",
                                        fontSize: "0.85rem",
                                        padding: 16,
                                    }}
                                ></div>
                            )}
                            {(column.children || []).map((element) => (
                                <div
                                    key={element.id}
                                    style={{
                                        ...element.style,
                                        border: "none",
                                        boxSizing: "border-box",
                                        display: 'flex',
                                        justifyContent: 'center',
                                        width: '100%'
                                    }}
                                >
                                    {element.type === "text" ||
                                        element.type === "h1" ||
                                        element.type === "h2" ? (
                                        <div
                                            dangerouslySetInnerHTML={{
                                                __html: (element.content || "").replace(
                                                    /\n/g,
                                                    "<br />"
                                                ),
                                            }}
                                        />
                                    ) : element.type === "image" ? (
                                        <img
                                            src={element.src}
                                            alt="Contenido de la lección"
                                            style={{
                                                maxWidth: "100%",
                                                height: element.style?.height || "auto",
                                                objectFit: "cover",
                                                borderRadius: 8,
                                            }}
                                        />
                                    ) : element.type === "video" ? (
                                        element.src ? (
                                            <video
                                                src={
                                                    element.src.startsWith("http")
                                                        ? element.src
                                                        : `${SERVER_URL}${element.src}`
                                                }
                                                controls
                                                controlsList="nodownload"
                                                onContextMenu={(e) => e.preventDefault()}
                                                style={{
                                                    width: element.style?.width || "100%",
                                                    height: element.style?.height || 300,
                                                    borderRadius: 8,
                                                    backgroundColor: "#000",
                                                }}
                                            />
                                        ) : (
                                            <div
                                                style={{
                                                    backgroundColor: "#000",
                                                    height: element.style?.height || 300,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    color: "#fff",
                                                    borderRadius: 8,
                                                }}
                                            >
                                                <span style={{ fontSize: "1.2rem" }}>
                                                    Video no configurado
                                                </span>
                                            </div>
                                        )
                                    ) : element.type === "iframe" ? (
                                        element.src ? (
                                            <iframe
                                                src={formatUrl(element.src)}
                                                title={element.content || "Contenido externo"}
                                                style={{
                                                    width: element.style?.width || "100%",
                                                    height: element.style?.height || "400px",
                                                    border: "none",
                                                    borderRadius: 8,
                                                }}
                                            />
                                        ) : (
                                            <div
                                                style={{
                                                    backgroundColor: "#f3f4f6",
                                                    height: element.style?.height || 400,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    color: "#6b7280",
                                                    borderRadius: 8,
                                                }}
                                            >
                                                <span style={{ fontSize: "1.2rem" }}>
                                                    Sitio web no configurado
                                                </span>
                                            </div>
                                        )
                                    ) : null}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        ));
    };

    const handleSelectOption = (idPregunta, idOpcion, permiteMultiple = false) => {
        setRespuestasUsuario((prev) => {
            const current = prev[idPregunta];

            if (permiteMultiple) {
                const currentArray = Array.isArray(current) ? current : (current ? [current] : []);
                const idString = String(idOpcion);

                // Verificar si ya está seleccionado
                const exists = currentArray.some(id => String(id) === idString);

                if (exists) {
                    return {
                        ...prev,
                        [idPregunta]: currentArray.filter(id => String(id) !== idString)
                    };
                } else {
                    return {
                        ...prev,
                        [idPregunta]: [...currentArray, idOpcion]
                    };
                }
            } else {
                return {
                    ...prev,
                    [idPregunta]: idOpcion,
                };
            }
        });
    };

    const handleSubmitQuiz = () => {
        let correctas = 0;
        let contestadas = 0;
        const total = quizPreguntas.length;

        quizPreguntas.forEach((pregunta) => {
            const idPregunta = pregunta.idPregunta;
            const descripcion =
                pregunta.descripcionPregunta ||
                pregunta.DescripcionPregunta ||
                pregunta.descripcion ||
                "";
            const tieneEspaciosBlanco = descripcion.includes("{}");

            if (tieneEspaciosBlanco) {
                // Lógica de espacios en blanco
                let tieneRespuesta = false;
                const respuestasBlanco = [];

                const blanksCount = descripcion.split('{}').length - 1;
                for (let i = 0; i < blanksCount; i++) {

                    const respuesta = respuestasUsuario[`${idPregunta}_blank_${i}`] || "";
                    if (respuesta && respuesta.trim()) {
                        tieneRespuesta = true;
                    }
                    respuestasBlanco.push(respuesta);
                }

                if (tieneRespuesta) {
                    contestadas++;
                    let esCorrecta = false;
                    let respuestasCorrectas = [];

                    if (pregunta.respuestaTextoLibre) {
                        const k = pregunta.respuestaTextoLibre.palabrasClave || "";
                        if (k) respuestasCorrectas = [k];
                    }
                    if (respuestasCorrectas.length === 0 && pregunta.opciones) {
                        respuestasCorrectas = pregunta.opciones
                            .filter(o => o.esCorrecta || o.correcta)
                            .map(o => o.descripcion || o.texto || "");
                    }

                    if (respuestasCorrectas.length > 0) {
                        esCorrecta = true;
                        for (let i = 0; i < respuestasBlanco.length; i++) {
                            const u = (respuestasBlanco[i] || "").trim().toLowerCase();
                            const c = (respuestasCorrectas[i] || respuestasCorrectas[0] || "").trim().toLowerCase();
                            if (u !== c) {
                                esCorrecta = false;
                                break;
                            }
                        }
                    } else {
                        esCorrecta = true; // Si no hay config, asumir correcto
                    }

                    if (esCorrecta) correctas++;
                }

            } else if (pregunta.tipo === "verdaderoFalso" || (pregunta.idTipoPregunta && parseInt(pregunta.idTipoPregunta) === 3)) {
                const val = respuestasUsuario[idPregunta];
                if (val !== undefined && val !== null) {
                    contestadas++;
                    const objVF = pregunta.respuestaVerdaderoFalso;
                    // Fix: Check property capitalization carefully
                    const correcta = objVF?.respuestaCorrecta !== undefined ? objVF.respuestaCorrecta : objVF?.RespuestaCorrecta;

                    // Comparar strings para evitar problemas de tipos
                    if (String(val) === String(correcta)) correctas++;
                }
            } else {
                // Opción múltiple
                const val = respuestasUsuario[idPregunta];
                if (!val || (Array.isArray(val) && val.length === 0)) return;
                contestadas++;

                const opciones = pregunta.opciones || [];
                let todasCorrectas = false;

                if (pregunta.permiteMultiple && Array.isArray(val)) {
                    const correctIds = opciones
                        .filter(o => o.esCorrecta || o.EsCorrecta)
                        .map(o => String(o.idOpcion || o.IdOpcion));

                    const selectedIds = val.map(String);

                    if (selectedIds.length === correctIds.length && selectedIds.every(id => correctIds.includes(id))) {
                        todasCorrectas = true;
                    }
                } else {
                    const selectedVal = val;
                    const seleccionada = opciones.find(o => String(o.idOpcion || o.IdOpcion) === String(selectedVal));
                    if (seleccionada) {
                        todasCorrectas = seleccionada.esCorrecta !== undefined ? seleccionada.esCorrecta : (seleccionada.EsCorrecta || false);
                    }
                }

                if (todasCorrectas) correctas++;
            }
        });

        const calificacion = total > 0 ? (correctas / total) * 100 : 0;
        const aprobado = Math.round(calificacion) >= 60;

        setResultsData({
            correctas,
            incorrectas: total - correctas,
            calificacion: Math.round(calificacion),
            aprobado
        });
        setQuizSubmitted(true);
        setShowResultsModal(true);
    };

    const handleQuizButtonClick = () => {
        if (!quizSubmitted) {
            handleSubmitQuiz();
        } else {
            goNext();
        }
    };

    const handleSubmitExam = () => {
        let correctas = 0;
        let contestadas = 0;
        const total = examPreguntas.length;

        examPreguntas.forEach((pregunta) => {
            const idPregunta = pregunta.idPregunta;
            const descripcion =
                pregunta.descripcionPregunta ||
                pregunta.DescripcionPregunta ||
                pregunta.descripcion ||
                "";
            const tieneEspaciosBlanco = descripcion.includes("{}");

            if (tieneEspaciosBlanco) {
                // Lógica de espacios en blanco
                let tieneRespuesta = false;
                const respuestasBlanco = [];

                const blanksCount = descripcion.split('{}').length - 1;
                for (let i = 0; i < blanksCount; i++) {

                    const respuesta = respuestasUsuario[`${idPregunta}_blank_${i}`] || "";
                    if (respuesta && respuesta.trim()) {
                        tieneRespuesta = true;
                    }
                    respuestasBlanco.push(respuesta);
                }

                if (tieneRespuesta) {
                    contestadas++;
                    let esCorrecta = false;
                    let respuestasCorrectas = [];

                    if (pregunta.respuestaTextoLibre) {
                        const k = pregunta.respuestaTextoLibre.palabrasClave || "";
                        if (k) respuestasCorrectas = [k];
                    }
                    if (respuestasCorrectas.length === 0 && pregunta.opciones) {
                        respuestasCorrectas = pregunta.opciones
                            .filter(o => o.esCorrecta || o.correcta)
                            .map(o => o.descripcion || o.texto || "");
                    }
                    if (respuestasCorrectas.length > 0) {
                        esCorrecta = true;
                        for (let i = 0; i < respuestasBlanco.length; i++) {
                            const u = (respuestasBlanco[i] || "").trim().toLowerCase();
                            const c = (respuestasCorrectas[i] || respuestasCorrectas[0] || "").trim().toLowerCase();
                            if (u !== c) {
                                esCorrecta = false;
                                break;
                            }
                        }
                    } else { esCorrecta = true; } // Default true

                    if (esCorrecta) correctas++;
                }

            } else if (pregunta.tipo === "verdaderoFalso" || (pregunta.idTipoPregunta && parseInt(pregunta.idTipoPregunta) === 3)) {
                const val = respuestasUsuario[idPregunta];
                if (val !== undefined && val !== null) {
                    contestadas++;
                    const objVF = pregunta.respuestaVerdaderoFalso;
                    const correcta = objVF?.respuestaCorrecta !== undefined ? objVF.respuestaCorrecta : objVF?.RespuestaCorrecta;
                    if (String(val) === String(correcta)) correctas++;
                }
            } else {
                const val = respuestasUsuario[idPregunta];
                if (!val || (Array.isArray(val) && val.length === 0)) return;
                contestadas++;

                const opciones = pregunta.opciones || [];
                let todasCorrectas = false;

                if (pregunta.permiteMultiple && Array.isArray(val)) {
                    const correctIds = opciones
                        .filter(o => o.esCorrecta || o.EsCorrecta)
                        .map(o => String(o.idOpcion || o.IdOpcion));

                    const selectedIds = val.map(String);

                    if (selectedIds.length === correctIds.length && selectedIds.every(id => correctIds.includes(id))) {
                        todasCorrectas = true;
                    }
                } else {
                    const selectedVal = val;
                    const seleccionada = opciones.find(o => String(o.idOpcion || o.IdOpcion) === String(selectedVal));
                    if (seleccionada) {
                        todasCorrectas = seleccionada.esCorrecta !== undefined ? seleccionada.esCorrecta : (seleccionada.EsCorrecta || false);
                    }
                }
                if (todasCorrectas) correctas++;
            }
        });

        const calificacion = total > 0 ? (correctas / total) * 100 : 0;
        const aprobado = Math.round(calificacion) >= 60;

        setResultsData({
            correctas,
            incorrectas: total - correctas,
            calificacion: Math.round(calificacion),
            aprobado
        });
        setExamSubmitted(true);
        setShowResultsModal(true);
    };

    const handleExamButtonClick = () => {
        if (!examSubmitted) {
            handleSubmitExam();
        } else {
            goNext();
        }
    };



    // Función helper para renderizar preguntas con espacios en blanco
    const renderQuestionWithBlanks = (descripcion, idPregunta, preguntaCompleta) => {
        console.log('=== DEBUG renderQuestionWithBlanks ===');
        console.log('Descripción:', descripcion);
        console.log('ID Pregunta:', idPregunta);
        console.log('Pregunta completa:', preguntaCompleta);

        // Si no hay {}, renderizar normalmente
        if (!descripcion.includes('{}')) {
            return <p style={{ margin: "0 0 0.75rem 0", color: "#0f172a" }}>{descripcion}</p>;
        }

        // Dividir la descripción por {}
        const parts = descripcion.split('{}');
        const blanksCount = parts.length - 1;

        console.log('Número de espacios en blanco:', blanksCount);

        // Obtener las respuestas correctas
        let correctAnswers = [];

        // Primero intentar obtener de respuestaTextoLibre (preguntas de texto libre)
        if (preguntaCompleta && preguntaCompleta.respuestaTextoLibre) {
            const palabrasClave = preguntaCompleta.respuestaTextoLibre.palabrasClave ||
                preguntaCompleta.respuestaTextoLibre.PalabrasClave ||
                "";
            console.log('Palabras clave encontradas:', palabrasClave);

            if (palabrasClave) {
                // Si hay múltiples espacios en blanco, dividir las palabras clave
                // Asumimos que están separadas por algún delimitador o es una sola respuesta
                correctAnswers = [palabrasClave];
            }
        }

        // Si no hay respuestas de texto libre, buscar en opciones (opción múltiple)
        if (correctAnswers.length === 0 && preguntaCompleta && preguntaCompleta.opciones) {
            console.log('Buscando en opciones:', preguntaCompleta.opciones);
            correctAnswers = preguntaCompleta.opciones
                .filter(o => o.esCorrecta || o.EsCorrecta || o.correcta)
                .map(o => o.descripcion || o.Descripcion || o.texto || o.Texto || "");
        }

        console.log('Respuestas correctas finales:', correctAnswers);

        // Console log para debugging - mostrar pregunta con respuestas
        let preguntaConRespuestas = descripcion;
        correctAnswers.forEach((respuesta, idx) => {
            preguntaConRespuestas = preguntaConRespuestas.replace('{}', `[${respuesta}]`);
        });
        console.log(`[Pregunta ID: ${idPregunta}] ${preguntaConRespuestas}`);

        return (
            <div style={{ margin: "0 0 0.75rem 0", color: "#0f172a", lineHeight: "2" }}>
                {parts.map((part, idx) => {
                    // Determinar qué valor mostrar en el input
                    const userAnswer = respuestasUsuario[`${idPregunta}_blank_${idx}`] || "";

                    // Obtener la respuesta correcta para este blank con fallback al primero si solo hay uno definido
                    const correctAnswer = (correctAnswers[idx] || correctAnswers[0] || "").trim().toLowerCase();
                    const maxLength = correctAnswer.length > 0 ? correctAnswer.length + 10 : 50;

                    // Determinar si la respuesta es correcta (comparación case-insensitive y trimmed)
                    // Si no hay respuestas correctas definidas (correctAnswers.length === 0), se considera correcta por defecto para coincidir con el backend
                    const isCorrect = correctAnswers.length === 0 || userAnswer.trim().toLowerCase() === correctAnswer;
                    const isSubmitted = quizSubmitted || examSubmitted;

                    // Determinar el color del borde según si es correcto o no
                    let borderColor = "#cbd5e1"; // Default
                    let backgroundColor = "#ffffff";

                    if (isSubmitted && userAnswer.trim()) {
                        borderColor = isCorrect ? "#10b981" : "#ef4444"; // Verde si correcto, rojo si incorrecto
                        backgroundColor = isCorrect ? "#f0fdf4" : "#fef2f2"; // Fondo verde claro o rojo claro
                    } else if (isSubmitted) {
                        // Si no contestó, pero se considera correcta por falta de definición
                        if (isCorrect && correctAnswers.length === 0) {
                            borderColor = "#10b981";
                            backgroundColor = "#f0fdf4";
                        } else {
                            backgroundColor = "#f1f5f9";
                        }
                    }

                    // Determinar qué valor mostrar en el input
                    let displayValue = userAnswer;

                    return (
                        <React.Fragment key={idx}>
                            <span>{part}</span>
                            {idx < blanksCount && (
                                <input
                                    type="text"
                                    style={{
                                        display: "inline-block",
                                        minWidth: "120px",
                                        padding: "4px 8px",
                                        margin: "0 4px",
                                        border: `2px solid ${borderColor}`,
                                        borderRadius: "4px",
                                        fontSize: "0.9rem",
                                        backgroundColor: backgroundColor,
                                        transition: "all 0.3s ease",
                                    }}
                                    placeholder="Escribe aquí..."
                                    maxLength={isSubmitted ? 200 : maxLength}
                                    value={displayValue}
                                    onChange={(e) => {
                                        if (!quizSubmitted && !examSubmitted) {
                                            setRespuestasUsuario(prev => ({
                                                ...prev,
                                                [`${idPregunta}_blank_${idx}`]: e.target.value
                                            }));
                                        }
                                    }}
                                    disabled={quizSubmitted || examSubmitted}
                                    title={""}
                                />
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
        );
    };

    const renderQuizView = () => {

        if (loadingQuestions) {
            return (
                <div style={{ textAlign: "center", padding: "2rem", color: "#64748b" }}>
                    Cargando preguntas del cuestionario...
                </div>
            );
        }

        if (!quizPreguntas || quizPreguntas.length === 0) {
            return (
                <div style={{ textAlign: "center", padding: "2rem", color: "#94a3b8" }}>
                    Este cuestionario aún no tiene preguntas configuradas.
                </div>
            );
        }

        return (
            <div style={{ marginTop: "1.5rem" }}>
                {quizPreguntas.map((pregunta, index) => {
                    const descripcion =
                        pregunta.descripcionPregunta ||
                        pregunta.DescripcionPregunta ||
                        pregunta.descripcion ||
                        "";

                    return (
                        <div
                            key={pregunta.idPregunta || index}
                            style={{
                                border: "1px solid #e2e8f0",
                                borderRadius: 8,
                                padding: "1rem 1.25rem",
                                marginBottom: "1rem",
                                backgroundColor: "#ffffff",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: "0.5rem",
                                }}
                            >
                                <h3 style={{ fontSize: "0.95rem", fontWeight: 600, margin: 0 }}>
                                    Pregunta {index + 1}
                                    {pregunta.permiteMultiple && (
                                        <span style={{ color: "#64748b", fontSize: "0.85rem", fontWeight: "normal", marginLeft: "8px" }}>
                                            (más de una respuesta)
                                        </span>
                                    )}
                                </h3>
                            </div>

                            {renderQuestionWithBlanks(descripcion, pregunta.idPregunta, pregunta)}

                            {/* Verdadero / Falso */}
                            {(pregunta.tipo === "verdaderoFalso" || (pregunta.idTipoPregunta && (pregunta.idTipoPregunta === 3 || pregunta.IdTipoPregunta === 3))) && (
                                <div style={{ display: 'flex', gap: '10px', marginTop: '1rem', marginBottom: '1rem' }}>
                                    {[true, false].map((val) => {
                                        const isTruth = val === true;
                                        const labelText = isTruth ? "Verdadero" : "Falso";
                                        const letter = isTruth ? "A" : "B";

                                        const userAns = respuestasUsuario[pregunta.idPregunta];
                                        const isSelected = String(userAns) === String(val);

                                        const correctAnswer = pregunta.respuestaVerdaderoFalso?.respuestaCorrecta !== undefined
                                            ? pregunta.respuestaVerdaderoFalso?.respuestaCorrecta
                                            : pregunta.respuestaVerdaderoFalso?.RespuestaCorrecta;

                                        const isCorrectAnswer = String(correctAnswer) === String(val);

                                        let backgroundColor = "transparent";
                                        let border = "1px solid #e2e8f0";
                                        let letterStyle = {};

                                        // Feedback V/F
                                        if (quizSubmitted) {
                                            if (isCorrectAnswer) {
                                                backgroundColor = "#ecfdf3";
                                                border = "1px solid #22c55e";
                                                letterStyle = { backgroundColor: '#10b981', borderColor: '#059669', color: 'white' };
                                            } else if (isSelected) {
                                                backgroundColor = "#fef2f2";
                                                border = "1px solid #ef4444";
                                                letterStyle = { borderColor: '#ef4444', color: '#ef4444', background: '#fff' };
                                            }
                                        } else if (isSelected) {
                                            // Verde al seleccionar durante el intento
                                            backgroundColor = "#ecfdf3";
                                            border = "1px solid #22c55e";
                                            letterStyle = { backgroundColor: '#10b981', borderColor: '#059669', color: 'white' };
                                        }

                                        return (
                                            <div
                                                key={String(val)}
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    padding: "0.4rem 0.6rem",
                                                    borderRadius: 6,
                                                    backgroundColor,
                                                    border,
                                                    flex: 1,
                                                    cursor: quizSubmitted ? "default" : "pointer",
                                                }}
                                                onClick={() => !quizSubmitted && handleSelectOption(pregunta.idPregunta, val)}
                                            >
                                                <span
                                                    style={{
                                                        display: "inline-flex",
                                                        width: 26,
                                                        height: 26,
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        borderRadius: "999px",
                                                        backgroundColor: "#e2e8f0",
                                                        fontSize: "0.8rem",
                                                        fontWeight: 600,
                                                        marginRight: 8,
                                                        ...letterStyle
                                                    }}
                                                >
                                                    {letter}
                                                </span>
                                                <span style={{ fontSize: "0.9rem", color: "#0f172a", fontWeight: '600' }}>{labelText}</span>

                                                {quizSubmitted && isSelected && isCorrectAnswer && (
                                                    <span style={{ marginLeft: "auto", fontSize: "0.75rem", color: "#16a34a", fontWeight: 600 }}>Correcta</span>
                                                )}
                                                {quizSubmitted && isSelected && !isCorrectAnswer && (
                                                    <span style={{ marginLeft: "auto", fontSize: "0.75rem", color: "#ef4444", fontWeight: 600 }}>Incorrecta</span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            <div>
                                {(pregunta.opciones || []).map((opcion, idx) => {
                                    const llave =
                                        opcion.llave ||
                                        opcion.Llave ||
                                        String.fromCharCode(65 + idx);
                                    const desc =
                                        opcion.descripcion ||
                                        opcion.Descripcion ||
                                        opcion.texto ||
                                        opcion.Texto ||
                                        "";
                                    const esCorrecta =
                                        opcion.esCorrecta !== undefined
                                            ? opcion.esCorrecta
                                            : opcion.EsCorrecta !== undefined
                                                ? opcion.EsCorrecta
                                                : false;

                                    const idPregunta = pregunta.idPregunta;
                                    const idOpcion = opcion.idOpcion || opcion.IdOpcion;
                                    const selectedVal = respuestasUsuario[idPregunta];
                                    const isMultiple = pregunta.permiteMultiple;
                                    let isSelected = false;

                                    if (isMultiple && Array.isArray(selectedVal)) {
                                        isSelected = selectedVal.some(id => String(id) === String(idOpcion));
                                    } else {
                                        isSelected = selectedVal && String(selectedVal) === String(idOpcion);
                                    }

                                    let backgroundColor = "transparent";
                                    let border = "1px solid #e2e8f0";
                                    let letterStyle = {};

                                    // Feedback Opción Múltiple
                                    if (quizSubmitted) {
                                        if (esCorrecta) {
                                            backgroundColor = "#ecfdf3";
                                            border = "1px solid #22c55e";
                                            letterStyle = { backgroundColor: '#10b981', borderColor: '#059669', color: 'white' };
                                        } else if (isSelected) {
                                            backgroundColor = "#fef2f2";
                                            border = "1px solid #ef4444";
                                            letterStyle = { borderColor: '#ef4444', color: '#ef4444', background: '#fff' };
                                        }
                                    } else if (isSelected) {
                                        // Verde al seleccionar durante el intento
                                        backgroundColor = "#ecfdf3";
                                        border = "1px solid #22c55e";
                                        letterStyle = { backgroundColor: '#10b981', borderColor: '#059669', color: 'white' };
                                    }

                                    return (
                                        <div
                                            key={opcion.idOpcion || opcion.IdOpcion || idx}
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                padding: "0.4rem 0.6rem",
                                                borderRadius: 6,
                                                backgroundColor,
                                                border,
                                                marginBottom: 6,
                                                cursor: "pointer",
                                            }}
                                            onClick={() =>
                                                !quizSubmitted &&
                                                idPregunta &&
                                                idOpcion &&
                                                handleSelectOption(idPregunta, idOpcion, isMultiple)
                                            }
                                        >
                                            <span
                                                style={{
                                                    display: "inline-flex",
                                                    width: 26,
                                                    height: 26,
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    borderRadius: "999px",
                                                    backgroundColor: "#e2e8f0",
                                                    fontSize: "0.8rem",
                                                    fontWeight: 600,
                                                    marginRight: 8,
                                                    ...letterStyle
                                                }}
                                            >
                                                {llave}
                                            </span>
                                            <span style={{ fontSize: "0.9rem", color: "#0f172a" }}>
                                                {desc}
                                            </span>
                                            {quizSubmitted && isSelected && esCorrecta && (
                                                <span
                                                    style={{
                                                        marginLeft: "auto",
                                                        fontSize: "0.75rem",
                                                        color: "#16a34a",
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    Correcta
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderExamView = () => {
        if (loadingQuestions) {
            return (
                <div style={{ textAlign: "center", padding: "2rem", color: "#64748b" }}>
                    Cargando preguntas del examen...
                </div>
            );
        }

        if (!examPreguntas || examPreguntas.length === 0) {
            return (
                <div style={{ textAlign: "center", padding: "2rem", color: "#94a3b8" }}>
                    Este examen aún no tiene preguntas configuradas.
                </div>
            );
        }

        return (
            <div style={{ marginTop: "1.5rem" }}>
                {examPreguntas.map((pregunta, index) => {
                    const descripcion =
                        pregunta.descripcionPregunta ||
                        pregunta.DescripcionPregunta ||
                        pregunta.descripcion ||
                        "";

                    return (
                        <div
                            key={pregunta.idPregunta || index}
                            style={{
                                border: "1px solid #e2e8f0",
                                borderRadius: 8,
                                padding: "1rem 1.25rem",
                                marginBottom: "1rem",
                                backgroundColor: "#ffffff",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: "0.5rem",
                                }}
                            >
                                <h3 style={{ fontSize: "0.95rem", fontWeight: 600, margin: 0 }}>
                                    Pregunta {index + 1}
                                    {pregunta.permiteMultiple && (
                                        <span style={{ color: "#64748b", fontSize: "0.85rem", fontWeight: "normal", marginLeft: "8px" }}>
                                            (más de una respuesta)
                                        </span>
                                    )}
                                </h3>
                            </div>

                            {renderQuestionWithBlanks(descripcion, pregunta.idPregunta, pregunta)}

                            {/* Verdadero / Falso */}
                            {(pregunta.tipo === "verdaderoFalso" || (pregunta.idTipoPregunta && (pregunta.idTipoPregunta === 3 || pregunta.IdTipoPregunta === 3))) && (
                                <div style={{ display: 'flex', gap: '10px', marginTop: '1rem', marginBottom: '1rem' }}>
                                    {[true, false].map((val) => {
                                        const isTruth = val === true;
                                        const labelText = isTruth ? "Verdadero" : "Falso";
                                        const letter = isTruth ? "A" : "B";

                                        const userAns = respuestasUsuario[pregunta.idPregunta];
                                        const isSelected = String(userAns) === String(val);

                                        const correctAnswer = pregunta.respuestaVerdaderoFalso?.respuestaCorrecta !== undefined
                                            ? pregunta.respuestaVerdaderoFalso?.respuestaCorrecta
                                            : pregunta.respuestaVerdaderoFalso?.RespuestaCorrecta;

                                        const isCorrectAnswer = String(correctAnswer) === String(val);

                                        let backgroundColor = "transparent";
                                        let border = "1px solid #e2e8f0";
                                        let letterStyle = {};

                                        // Feedback Examen V/F
                                        if (examSubmitted) {
                                            if (isCorrectAnswer) {
                                                backgroundColor = "#ecfdf3";
                                                border = "1px solid #22c55e";
                                                letterStyle = { backgroundColor: '#10b981', borderColor: '#059669', color: 'white' };
                                            } else if (isSelected) {
                                                backgroundColor = "#fef2f2";
                                                border = "1px solid #ef4444";
                                                letterStyle = { borderColor: '#ef4444', color: '#ef4444', background: '#fff' };
                                            }
                                        } else if (isSelected) {
                                            // Verde al seleccionar durante el intento
                                            backgroundColor = "#ecfdf3";
                                            border = "1px solid #22c55e";
                                            letterStyle = { backgroundColor: '#10b981', borderColor: '#059669', color: 'white' };
                                        }

                                        return (
                                            <div
                                                key={String(val)}
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    padding: "0.4rem 0.6rem",
                                                    borderRadius: 6,
                                                    backgroundColor,
                                                    border,
                                                    flex: 1,
                                                    cursor: examSubmitted ? "default" : "pointer",
                                                }}
                                                onClick={() => !examSubmitted && handleSelectOption(pregunta.idPregunta, val)}
                                            >
                                                <span
                                                    style={{
                                                        display: "inline-flex",
                                                        width: 26,
                                                        height: 26,
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        borderRadius: "999px",
                                                        backgroundColor: "#e2e8f0",
                                                        fontSize: "0.8rem",
                                                        fontWeight: 600,
                                                        marginRight: 8,
                                                        ...letterStyle
                                                    }}
                                                >
                                                    {letter}
                                                </span>
                                                <span style={{ fontSize: "0.9rem", color: "#0f172a", fontWeight: '600' }}>{labelText}</span>
                                                {examSubmitted && isSelected && isCorrectAnswer && (
                                                    <span style={{ marginLeft: "auto", fontSize: "0.75rem", color: "#16a34a", fontWeight: 600 }}>Correcta</span>
                                                )}
                                                {examSubmitted && isSelected && !isCorrectAnswer && (
                                                    <span style={{ marginLeft: "auto", fontSize: "0.75rem", color: "#ef4444", fontWeight: 600 }}>Incorrecta</span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            <div>
                                {(pregunta.opciones || []).map((opcion, idx) => {
                                    const llave =
                                        opcion.llave ||
                                        opcion.Llave ||
                                        String.fromCharCode(65 + idx);
                                    const desc =
                                        opcion.descripcion ||
                                        opcion.Descripcion ||
                                        opcion.texto ||
                                        opcion.Texto ||
                                        "";
                                    const esCorrecta =
                                        opcion.esCorrecta !== undefined
                                            ? opcion.esCorrecta
                                            : opcion.EsCorrecta !== undefined
                                                ? opcion.EsCorrecta
                                                : false;

                                    const idPregunta = pregunta.idPregunta;
                                    const idOpcion = opcion.idOpcion || opcion.IdOpcion;
                                    const selectedVal = respuestasUsuario[idPregunta];
                                    const isMultiple = pregunta.permiteMultiple;
                                    let isSelected = false;

                                    if (isMultiple && Array.isArray(selectedVal)) {
                                        isSelected = selectedVal.some(id => String(id) === String(idOpcion));
                                    } else {
                                        isSelected = selectedVal && String(selectedVal) === String(idOpcion);
                                    }

                                    let backgroundColor = "transparent";
                                    let border = "1px solid #e2e8f0";
                                    let letterStyle = {};

                                    // Feedback Examen Opción Múltiple
                                    if (examSubmitted) {
                                        if (esCorrecta) {
                                            backgroundColor = "#ecfdf3";
                                            border = "1px solid #22c55e";
                                            letterStyle = { backgroundColor: '#10b981', borderColor: '#059669', color: 'white' };
                                        } else if (isSelected) {
                                            backgroundColor = "#fef2f2";
                                            border = "1px solid #ef4444";
                                            letterStyle = { borderColor: '#ef4444', color: '#ef4444', background: '#fff' };
                                        }
                                    } else if (isSelected) {
                                        // Verde al seleccionar durante el intento
                                        backgroundColor = "#ecfdf3";
                                        border = "1px solid #22c55e";
                                        letterStyle = { backgroundColor: '#10b981', borderColor: '#059669', color: 'white' };
                                    }

                                    return (
                                        <div
                                            key={opcion.idOpcion || opcion.IdOpcion || idx}
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                padding: "0.4rem 0.6rem",
                                                borderRadius: 6,
                                                backgroundColor,
                                                border,
                                                marginBottom: 6,
                                                cursor: "pointer",
                                            }}
                                            onClick={() =>
                                                !examSubmitted &&
                                                idPregunta &&
                                                idOpcion &&
                                                handleSelectOption(idPregunta, idOpcion, isMultiple)
                                            }
                                        >
                                            <span
                                                style={{
                                                    display: "inline-flex",
                                                    width: 26,
                                                    height: 26,
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    borderRadius: "999px",
                                                    backgroundColor: "#e2e8f0",
                                                    fontSize: "0.8rem",
                                                    fontWeight: 600,
                                                    marginRight: 8,
                                                    ...letterStyle
                                                }}
                                            >
                                                {llave}
                                            </span>
                                            <span style={{ fontSize: "0.9rem", color: "#0f172a" }}>
                                                {desc}
                                            </span>
                                            {examSubmitted && isSelected && esCorrecta && (
                                                <span
                                                    style={{
                                                        marginLeft: "auto",
                                                        fontSize: "0.75rem",
                                                        color: "#16a34a",
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    Correcta
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const getIconForType = (tipo) => {
        switch (tipo) {
            case "LECCION":
                return FiPlayCircle;
            case "CUESTIONARIO":
                return FiHelpCircle;
            case "EXAMEN":
                return FiClipboard;
            default:
                return FiPlayCircle;
        }
    };


    // ---------- RENDER ----------

    return (
        <div className="cp-page">
            {/* TOPBAR */}
            <header className="cp-topbar">
                <button
                    type="button"
                    className="cp-topbar-close"
                    onClick={() => navigate(`/menu/lms/curso-curriculo-revision-view/${idCurso}`, { state: { fromCarreraId } })}
                    title="Volver al currículum"
                >
                    <FiChevronLeft />
                </button>

                <div className="cp-topbar-title">
                    <span className="cp-topbar-course">
                        {course?.titulo || course?.nombre || "Curso"} (Vista previa)
                    </span>
                </div>

                <div className="cp-topbar-close cp-topbar-close-right" />
            </header>

            <div
                className={
                    "cp-layout" + (sidebarOpen ? "" : " cp-layout--collapsed")
                }
            >
                {/* SIDEBAR */}
                <div className="cp-sidebar-wrapper">
                    <aside className="cp-sidebar">
                        <div className="cp-sidebar-list">
                            {modulesWithLessons.map((mod) => (
                                <div key={mod.moduleId} className="cp-sidebar-module">
                                    <div className="cp-sidebar-module-header">
                                        <span className="cp-sidebar-module-title">
                                            {mod.moduleTitle}
                                        </span>
                                        <span className="cp-sidebar-module-count">
                                            {mod.items.length}
                                        </span>
                                    </div>

                                    <div className="cp-sidebar-module-lessons">
                                        {mod.items.map((item) => {
                                            const isActive =
                                                (item.tipo === "LECCION" &&
                                                    String(item.lessonId) === String(currentLessonId)) ||
                                                (item.tipo !== "LECCION" &&
                                                    currentContentType === item.tipo &&
                                                    String(currentContentId) === String(item.id));

                                            const Icon = getIconForType(item.tipo);
                                            const isClickable = true;

                                            return (
                                                <button
                                                    key={`${item.tipo}-${item.id}`}
                                                    type="button"
                                                    className={
                                                        "cp-sidebar-lesson" +
                                                        (isActive ? " active" : "") +
                                                        (item.completado ? " completed" : "") +
                                                        (!isClickable ? " disabled" : "")
                                                    }
                                                    onClick={() =>
                                                        isClickable && handleClickContent(item)
                                                    }
                                                    disabled={!isClickable}
                                                    style={{
                                                        cursor: isClickable ? "pointer" : "default",
                                                        opacity: isClickable ? 1 : 0.7,
                                                    }}
                                                >
                                                    <span className="cp-sidebar-lesson-icon">
                                                        {item.completado && item.tipo === "LECCION" ? (
                                                            <FiCheckCircle />
                                                        ) : (
                                                            <Icon />
                                                        )}
                                                    </span>
                                                    <span className="cp-sidebar-lesson-title">
                                                        {item.lessonTitle}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}

                            {modulesWithLessons.length === 0 && (
                                <p className="cp-sidebar-empty">
                                    Este curso aún no tiene lecciones configuradas.
                                </p>
                            )}
                        </div>
                    </aside>

                    <button
                        type="button"
                        className={
                            "cp-sidebar-toggle" +
                            (sidebarOpen ? "" : " cp-sidebar-toggle--collapsed")
                        }
                        onClick={() => setSidebarOpen((prev) => !prev)}
                        aria-label={
                            sidebarOpen
                                ? "Ocultar lista de lecciones"
                                : "Mostrar lista de lecciones"
                        }
                    >
                        <span className="cp-sidebar-toggle-icon">
                            {sidebarOpen ? "‹" : "›"}
                        </span>
                    </button>
                </div>

                {/* MAIN */}
                <main className="cp-main">
                    {justCompletedTitle && (
                        <div className="cp-alert-success">
                            ¡Enhorabuena! Has completado «{justCompletedTitle}».
                        </div>
                    )}

                    {currentContentType === "CUESTIONARIO" ? (
                        <>
                            <h1 className="cp-lesson-title">
                                {currentContentTitle || "Cuestionario"}
                            </h1>
                            <p className="cp-lesson-subtitle">
                                {flatAllContent[currentIndexInAll]?.moduleTitle} · {currentIndexInAll + 1} de{" "}
                                {flatAllContent.length} elementos
                            </p>
                            <div
                                style={{
                                    marginTop: "1.5rem",
                                    marginBottom: "2rem",
                                    boxShadow: "0 1px 3px rgba(15,23,42,0.08)",
                                    borderRadius: "8px",
                                    padding: "1rem 1.25rem",
                                    backgroundColor: "#f8fafc",
                                }}
                            >
                                {renderQuizView()}
                            </div>
                            <div className="cp-lesson-actions">
                                <button
                                    type="button"
                                    className="cp-nav-btn"
                                    onClick={handleQuizButtonClick}
                                    disabled={quizPreguntas.length === 0}
                                >
                                    {!quizSubmitted ? 'Simular Calificación' : (currentIndexInAll >= flatAllContent.length - 1 ? 'Finalizar Revisión' : 'Siguiente')} <FiChevronRight />
                                </button>
                            </div>
                        </>
                    ) : currentContentType === "EXAMEN" ? (
                        <>
                            <h1 className="cp-lesson-title">
                                {currentContentTitle || "Examen"}
                            </h1>
                            <p className="cp-lesson-subtitle">
                                {flatAllContent[currentIndexInAll]?.moduleTitle} · {currentIndexInAll + 1} de{" "}
                                {flatAllContent.length} elementos
                            </p>
                            <div
                                style={{
                                    marginTop: "1.5rem",
                                    marginBottom: "2rem",
                                    boxShadow: "0 1px 3px rgba(15,23,42,0.08)",
                                    borderRadius: "8px",
                                    padding: "1rem 1.25rem",
                                    backgroundColor: "#f8fafc",
                                }}
                            >
                                {renderExamView()}
                            </div>
                            <div className="cp-lesson-actions">
                                <button
                                    type="button"
                                    className="cp-nav-btn"
                                    onClick={handleExamButtonClick}
                                    disabled={examPreguntas.length === 0}
                                >
                                    {!examSubmitted ? 'Simular Calificación' : (currentIndexInAll >= flatAllContent.length - 1 ? 'Finalizar Revisión' : 'Siguiente')} <FiChevronRight />
                                </button>
                            </div>
                        </>
                    ) : lessonSections && lessonSections.length > 0 ? (
                        <>
                            <h1 className="cp-lesson-title">
                                {currentItem?.lessonTitle || "Lección"}
                            </h1>

                            {currentItem && (
                                <p className="cp-lesson-subtitle">
                                    {flatAllContent[currentIndexInAll]?.moduleTitle} · {currentIndexInAll + 1} de{" "}
                                    {flatAllContent.length} elementos
                                </p>
                            )}

                            {/* Contenido real de la lección desde BD */}
                            <div style={{
                                marginTop: "1.5rem",
                                marginBottom: "2rem",
                                boxShadow: "0 1px 3px rgba(15,23,42,0.08)",
                                borderRadius: "8px",
                                overflow: "hidden"
                            }}>
                                {renderLessonSections()}
                            </div>

                            {currentItem && (
                                <>
                                    <div className="cp-lesson-nav">
                                        <button
                                            type="button"
                                            className="cp-nav-btn"
                                            disabled={currentIndexInAll <= 0}
                                            onClick={goPrev}
                                        >
                                            <FiChevronLeft /> Anterior
                                        </button>
                                        <button
                                            type="button"
                                            className="cp-nav-btn"
                                            onClick={goNext}
                                        >
                                            {currentIndexInAll >= flatAllContent.length - 1 ? 'Finalizar Revisión' : 'Siguiente'} <FiChevronRight />
                                        </button>
                                    </div>
                                </>
                            )}
                        </>
                    ) : (
                        <div className="cp-empty-main">
                            <p>No hay contenido seleccionado en este curso todavía.</p>
                        </div>
                    )
                    }
                </main >
            </div >
            {showResultsModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999,
                    backdropFilter: 'blur(4px)'
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '16px',
                        padding: '2rem',
                        width: '90%',
                        maxWidth: '400px',
                        textAlign: 'center',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                    }}>
                        <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e293b', marginBottom: '1.5rem' }}>
                            Resultados
                        </h2>

                        <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', marginBottom: '2rem' }}>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '2rem', fontWeight: '700', color: '#22c55e' }}>
                                    {resultsData.correctas}
                                </div>
                                <div style={{ fontSize: '0.875rem', color: '#64748b' }}>Correctas</div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '2rem', fontWeight: '700', color: '#ef4444' }}>
                                    {resultsData.incorrectas}
                                </div>
                                <div style={{ fontSize: '0.875rem', color: '#64748b' }}>Incorrectas</div>
                            </div>
                        </div>

                        <div style={{ marginBottom: '2rem' }}>
                            <div style={{
                                fontSize: '4rem',
                                fontWeight: '800',
                                color: resultsData.aprobado ? '#22c55e' : '#ef4444',
                                lineHeight: 1
                            }}>
                                {resultsData.calificacion}
                            </div>
                            <div style={{
                                fontSize: '1.25rem',
                                fontWeight: '600',
                                color: resultsData.aprobado ? '#22c55e' : '#ef4444',
                                marginTop: '0.5rem'
                            }}>
                                {resultsData.aprobado ? '¡Aprobado!' : 'No aprobado'}
                            </div>
                        </div>

                        <button
                            onClick={() => setShowResultsModal(false)}
                            style={{
                                backgroundColor: '#0f172a',
                                color: 'white',
                                padding: '0.75rem 2rem',
                                borderRadius: '8px',
                                border: 'none',
                                fontSize: '1rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                width: '100%',
                                transition: 'background-color 0.2s'
                            }}
                            onMouseOver={(e) => e.target.style.backgroundColor = '#1e293b'}
                            onMouseOut={(e) => e.target.style.backgroundColor = '#0f172a'}
                        >
                            Cerrar
                        </button>
                    </div>
                </div>
            )}
        </div >
    );
};

export default CoursePlayerReviewView;
